<script lang="ts">
import '@/styles';
import { RouterLink } from 'vue-router';

export const { defineRouteLayout } = exportRoute();

defineRouteLayout(undefined);
</script>

<script setup lang="ts">
import { useHead } from '@unhead/vue';

import LocaleSwitch from '@/components/LocaleSwitch.vue';
import NavBar from '@/components/NavBar.vue';
import RouterQueryView from '@/core/RouterQueryView.vue';
import { exportRoute } from '@/core/routing';

useHead({
  titleTemplate: $t`Castalian Spring`,
});
</script>

<template>
  <div :class="[$style.root, 'container-sm']">
    <header :class="$style.header">
      <h1>
        <RouterLink
          to="/"
          :title="$t`Castalian Spring`"
          :class="$style.homeLink"
        />
      </h1>

      <LocaleSwitch />
    </header>

    <ul :class="$style.nav">
      <li class="booking">
        <RouterLink to="?nav" class="link-body">{{
          $t`My\naccount`
        }}</RouterLink>
      </li>

      <li
        v-for="page in [
          'rooms',
          'service',
          'equip',
          'contact',
          'who-we-are',
          'news',
        ]"
        :key="page"
        :class="page"
      >
        <RouterLink :to="`/${page}`" class="link-body">{{
          $t`pages:${page}`
        }}</RouterLink>
      </li>

      <li class="timetable">
        <RouterLink class="btn btn-dark btn-intro" to="/timetable">{{
          $t`Book online`
        }}</RouterLink>
      </li>
    </ul>

    <div :class="$style.social">
      <a
        class="link-body btn-underline"
        href="https://www.facebook.com/castalianspring"
        target="_blank"
        ><span>Facebook</span></a
      >
      <a
        class="link-body btn-underline"
        href="https://instagram.com/castalianspring"
        target="_blank"
        ><span>Instagram</span></a
      >
    </div>

    <div :class="$style.legal">
      <RouterLink to="/impressum" class="link-body btn-underline">
        <span>{{ $t`Impressum` }}</span>
      </RouterLink>
    </div>

    <RouterQueryView v-slot="{ back }" name="nav" :leave-duration="500">
      <NavBar :back="back" no-locale-switch />
    </RouterQueryView>
  </div>
</template>

<style lang="scss" module>
html:has(.root) {
  background: fixed url('@/assets/images/home-bg.jpg') no-repeat center / cover;

  body {
    background-color: transparent;
  }
}

.root {
  @include media-breakpoint-up(md) {
    min-height: 100vh;
    position: relative;
  }

  @include media-breakpoint-down(md) {
    padding-top: $spacer;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.header {
  position: relative;

  @include media-breakpoint-up(md) {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }

  @include media-breakpoint-down(md) {
    margin-bottom: $spacer * 2;
  }

  h1 {
    margin-bottom: 0;
  }

  .LocaleSwitch {
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translate(0, -50%);
  }

  .LocaleSwitchBtn {
    @include intro-font;

    --#{$prefix}btn-padding-y: 0;
    --#{$prefix}btn-font-size: #{$font-size-intro};
  }
}

.homeLink {
  display: block;
  background: url('@/assets/images/home-logo.svg') no-repeat center / cover;
  width: 274px;
  height: 102px;

  @include media-breakpoint-between(xxs, lg) {
    width: 220px;
    height: 82px;
  }

  @include media-breakpoint-down(xxs) {
    width: 180px;
    height: 67px;
  }
}

.nav {
  @include list-unstyled;

  margin: 0;

  :global a:not(.btn) {
    @include underline-btn($inner-selector: 'span');
    @include intro-font;

    font-size: $font-size-intro;
    transition: $btn-transition;
    padding: 0.75em 1em;
    max-width: 18em;

    --bs-link-color-rgb: #{to-rgb($body-color)};

    @include media-breakpoint-up(md) {
      white-space: pre;
    }
  }

  @include media-breakpoint-up(md) {
    $right-width: 14em;

    :global li {
      width: 33.33%;
      height: 33.33%;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      position: absolute;
      flex-direction: column;

      &.timetable {
        align-items: center;
        left: 50%;
        transform: translateX(-50%);
      }

      &.service {
        right: 0;
        align-items: flex-start;
        width: $right-width;
      }

      &.who-we-are {
        top: 50%;
        transform: translateY(-50%);
      }

      &.contact {
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        align-items: flex-start;
        width: $right-width;
      }

      &.rooms {
        bottom: 0;
      }

      &.equip {
        align-items: center;
        left: 50%;
        bottom: 0;
        transform: translateX(-50%);
      }

      &.news {
        right: 0;
        bottom: 0;
        align-items: flex-start;
        width: $right-width;
      }
    }
  }

  @include media-breakpoint-down(md) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: $spacer * 2;

    :global li.timetable {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      flex-direction: column;
    }
  }

  @include media-breakpoint-between(sm, md) {
    gap: $spacer * 1.5;

    :global a:not(.btn) {
      font-size: $font-size-intro-lg;
    }

    :global li.timetable .btn.btn-intro {
      --#{$prefix}btn-font-size: #{$font-size-intro-lg};
      --#{$prefix}btn-padding-y: #{$input-btn-padding-y-lg * 2};
      --#{$prefix}btn-padding-x: #{$input-btn-padding-x-lg};
    }
  }

  @include media-breakpoint-down(sm) {
    gap: $spacer;
  }
}

.legal,
.social {
  display: flex;
  gap: 1rem;
  margin-bottom: $spacer * 0.5;

  > a {
    @include intro-font;
  }

  font-size: $font-size-intro-sm;

  @include media-breakpoint-between(sm, md) {
    font-size: $font-size-intro;
  }

  @include media-breakpoint-up(md) {
    position: absolute;
    bottom: 1rem;
  }
}

@include media-breakpoint-up(md) {
  .legal {
    left: 2rem;
  }

  .social {
    right: 2rem;
  }
}
</style>
